import { Component, Input, Output, EventEmitter, inject, signal, OnInit, OnDestroy, ElementRef, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatStateService } from '../../services';
import { ChatApiService } from '../../services/chat-api.service';
import { SessionService } from '../../services/session.service';
import { ChatWindowComponent } from '../chat-window/chat-window';
import { ChatUser } from '../../models';

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatWindowComponent],
  templateUrl: './chat-widget.html',
  styleUrls: ['./chat-widget.scss'],
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  @Input() userId!: string;
  @Input() userDisplayName: string = '';
  @Output() minimized = new EventEmitter<void>();

  readonly state = inject(ChatStateService);
  readonly sessionService = inject(SessionService);
  private readonly api = inject(ChatApiService);
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  isPickingUser = signal(true);
  sessionError = signal<string | null>(null);

  // Drag state
  private dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private panelX: number | null = null;
  private panelY: number | null = null;
  private moveListener: (() => void) | null = null;
  private upListener: (() => void) | null = null;

  ngOnInit(): void {
    if (!this.userId) {
      this.sessionError.set('userId must be provided to the chat widget');
      console.error('ChatWidgetComponent: userId @Input is required');
      return;
    }

    try {
      this.sessionService.initializeSession(this.userId);

      const displayName = this.userDisplayName || `User ${this.userId}`;
      this.api.ensureUser(this.userId, displayName).subscribe({
        next: (user) => {
          this.state.init(user);
          this.state.loadUsers();
          this.isPickingUser.set(false);
        },
        error: (err) => {
          console.error('Chat API unreachable', err);
          this.sessionError.set('Unable to connect to chat service. Please try again later.');
        },
      });
    } catch (error: any) {
      this.sessionError.set(error.message);
      console.error('Session initialization failed:', error);
    }
  }

  ngOnDestroy(): void {
    this.sessionService.endSession();
    this.removeDragListeners();
  }

  minimize(): void {
    this.minimized.emit();
  }

  pickUser(user: ChatUser): void {
    if (!this.sessionService.isSessionValid()) {
      this.sessionError.set('Session is no longer valid');
      return;
    }
    this.state.init(user);
    this.isPickingUser.set(false);
  }

  onDragStart(event: MouseEvent): void {
    event.preventDefault();
    const panel = this.el.nativeElement.querySelector('.chat-panel') as HTMLElement;
    if (!panel) return;

    this.dragging = true;
    const rect = panel.getBoundingClientRect();
    this.dragOffsetX = event.clientX - rect.left;
    this.dragOffsetY = event.clientY - rect.top;

    this.moveListener = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => this.onDragMove(e));
    this.upListener = this.renderer.listen('document', 'mouseup', () => this.onDragEnd());
  }

  private onDragMove(event: MouseEvent): void {
    if (!this.dragging) return;
    this.panelX = event.clientX - this.dragOffsetX;
    this.panelY = event.clientY - this.dragOffsetY;

    const panel = this.el.nativeElement.querySelector('.chat-panel') as HTMLElement;
    if (panel) {
      panel.style.left = `${this.panelX}px`;
      panel.style.top = `${this.panelY}px`;
    }
  }

  private onDragEnd(): void {
    this.dragging = false;
    this.removeDragListeners();
  }

  private removeDragListeners(): void {
    if (this.moveListener) { this.moveListener(); this.moveListener = null; }
    if (this.upListener) { this.upListener(); this.upListener = null; }
  }
}
